<template>
    <div>
        <ts-page-title
            :title="$t('bankProfile.pageTitle')"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                { text: $t('bankProfile.pageTitle'), active: true }
            ]"
        />
        <ts-panel>
            <ts-panel-wrapper
                class="tw-flex tw-items-center tw-justify-between"
            >
                <Button
                    type="info"
                    ghost
                    @click="$router.push({ name: 'bank-profile-create' })"
                >
                    {{ $t("addNew") }}
                </Button>
                <Input
                    search
                    v-model="search"
                    :placeholder="$t('bankProfile.search')"
                    style="width: 300px"
                />
            </ts-panel-wrapper>

            <Table
                :columns="columns"
                :data="resources"
                size="small"
                :loading="loading"
                stripe
            >
                <template slot-scope="{ row }" slot="action">
                    <a
                        href="#"
                        class="text-success"
                        @click.prevent="onAddAccount(row)"
                        v-tooltip="$t('bankProfile.addAccount')"
                    >
                        <i class="fas fa-plus-circle" style="font-size: 18px;"></i>
                    </a>
                    <a
                        href="#"
                        class="text-primary ml-2"
                        @click.prevent="onEdit(row)"
                        v-tooltip="$t('edit')"
                    >
                        <Icon type="ios-create" size="20" />
                    </a>
                    <Poptip
                        :title="$t('are_you_sure_to_delete')"
                        :transfer="true"
                        width="200"
                    >
                        <a
                            class="ml-2 text-danger"
                            v-tooltip="$t('delete')"
                            :disabled="row._deleting"
                        >
                            <i class="fas fa-circle-notch fa-spin" v-if="row._deleting"></i>
                            <Icon type="ios-trash" size="20" v-else />
                        </a>
                        <div slot="content" class="tw-text-right tw-mt-2">
                            <a
                                class="ivu-btn ivu-btn-danger ivu-btn-small"
                                @click.prevent="onDelete(row)"
                            >
                                <span>{{ $t("ok") }}</span>
                            </a>
                        </div>
                    </Poptip>
                </template>
            </Table>

            <div class="tw-flex tw-items-center tw-justify-end tw-p-4">
                <ts-i-pagination
                    v-model="pagination"
                    @navigate="fetchData"
                ></ts-i-pagination>
            </div>
        </ts-panel>

        <!-- Quick Add Account modal -->
        <Modal
            v-model="addAccountModal"
            :title="addAccountTitle"
            :mask-closable="false"
            :width="520"
            @on-cancel="onAddAccountCancel"
        >
            <div v-if="addAccountBank">
                <div class="tw-mb-3">
                    <label class="form-col-label control-label">{{ $t('bankProfile.employeeId') }}</label>
                    <Select
                        v-model="newAccount.employee_id"
                        filterable
                        remote
                        clearable
                        transfer
                        :remote-method="searchEmployeeForNew"
                        :loading="loadingEmployee"
                        :placeholder="$t('bankProfile.searchEmployee')"
                        @on-open-change="opened => opened && searchEmployeeForNew('')"
                    >
                        <Option
                            v-for="emp in employeeOptions"
                            :key="emp.employee_id"
                            :value="emp.employee_id"
                            :label="emp.employee_name_en || emp.employee_name_kh"
                        >
                            <span class="tw-font-mono tw-text-gray-400 tw-text-xs tw-mr-1">{{ emp.card_id }}</span>
                            <span class="tw-text-gray-800">{{ emp.employee_name_en }}</span>
                            <span v-if="emp.employee_name_kh" class="tw-text-gray-400 tw-text-xs tw-ml-1">{{ emp.employee_name_kh }}</span>
                        </Option>
                    </Select>
                </div>

                <div class="tw-mb-3">
                    <label class="required form-col-label control-label">{{ $t('bankProfile.accountNo') }}</label>
                    <Input
                        v-model="newAccount.account_no"
                        :placeholder="$t('bankProfile.accountNo')"
                        :class="{ 'is-invalid': errors.has('account_no') }"
                    />
                    <div class="invalid-feedback" v-if="errors.has('account_no')">
                        {{ errors.first('account_no') }}
                    </div>
                </div>

                <div class="tw-mb-3">
                    <label class="required form-col-label control-label">{{ $t('bankProfile.accountName') }}</label>
                    <Input
                        v-model="newAccount.account_name"
                        :placeholder="$t('bankProfile.accountName')"
                        :class="{ 'is-invalid': errors.has('account_name') }"
                    />
                    <div class="invalid-feedback" v-if="errors.has('account_name')">
                        {{ errors.first('account_name') }}
                    </div>
                </div>

                <div class="tw-mb-3">
                    <label class="form-col-label control-label tw-block tw-mb-1">{{ $t('bankProfile.qrImage') }}</label>
                    <div class="tw-flex tw-items-center tw-gap-3">
                        <img
                            v-if="newAccount.qr_image"
                            :src="newAccount.qr_image"
                            class="tw-w-16 tw-h-16 tw-object-cover tw-rounded tw-border tw-border-gray-200"
                        />
                        <label class="tw-inline-flex tw-items-center tw-px-3 tw-py-1 tw-bg-blue-50 hover:tw-bg-blue-100 tw-rounded tw-cursor-pointer tw-text-blue-600 tw-text-sm">
                            <i v-if="uploadingQr" class="fas fa-circle-notch fa-spin tw-mr-1"></i>
                            <i v-else class="fas fa-qrcode tw-mr-1"></i>
                            {{ $t('bankProfile.uploadQr') }}
                            <input
                                type="file"
                                accept="image/*"
                                class="tw-hidden"
                                :disabled="uploadingQr"
                                @change="onQrChange"
                            />
                        </label>
                        <a
                            v-if="newAccount.qr_image"
                            href="#"
                            class="tw-text-red-500 tw-text-sm"
                            @click.prevent="newAccount.qr_image = null"
                        >
                            <i class="fas fa-times tw-mr-1"></i>{{ $t('delete') }}
                        </a>
                    </div>
                </div>

                <div class="tw-mb-1 tw-flex tw-items-center tw-gap-2">
                    <i-switch v-model="newAccount.is_receive_cash_transfer" />
                    <span class="tw-text-sm">{{ $t('bankProfile.isReceiveCashTransfer') }}</span>
                </div>
            </div>

            <div slot="footer">
                <ts-button class="btn-gray" @click.prevent="onAddAccountCancel">{{ $t('cancel') }}</ts-button>
                <ts-button color="primary" :waiting="savingAccount" :disabled="savingAccount" @click.prevent="onSaveAccount">{{ $t('save') }}</ts-button>
            </div>
        </Modal>
    </div>
</template>

<script>
import { mapState } from "vuex";
import { debounce } from "lodash";
import { Errors } from "form-backend-validation";
import expandRow from "./table-expand.vue";

const emptyAccount = () => ({
    employee_id: null,
    account_no: "",
    account_name: "",
    qr_image: null,
    is_receive_cash_transfer: false
});

export default {
    name: "BankProfileIndex",
    data() {
        return {
            loading: false,
            addAccountModal: false,
            addAccountBank: null,
            newAccount: emptyAccount(),
            savingAccount: false,
            uploadingQr: false,
            employeeOptions: [],
            loadingEmployee: false,
            errors: new Errors()
        };
    },
    computed: {
        ...mapState("finance/bankProfile", ["resources", "pagination"]),
        addAccountTitle() {
            return this.addAccountBank
                ? `${this.$t('bankProfile.addAccount')} — ${this.addAccountBank.bank_name}`
                : this.$t('bankProfile.addAccount');
        },
        search: {
            get() {
                return this.$store.state.finance.bankProfile.search;
            },
            set(value) {
                this.$store.commit("finance/bankProfile/SET_SEARCH", value);
                this.$store.commit("finance/bankProfile/RESET_CURRENT_PAGE");
            }
        },
        columns() {
            return [
                {
                    type: "expand",
                    width: 50,
                    render: (h, params) => {
                        return h(expandRow, { props: { row: params.row } });
                    }
                },
                {
                    title: "#",
                    key: "bank_id",
                    width: 70,
                    align: "center"
                },
                {
                    title: this.$t("bankProfile.bankName"),
                    key: "bank_name",
                    minWidth: 200
                },
                {
                    title: this.$t("bankProfile.accounts"),
                    key: "accounts",
                    minWidth: 120,
                    render: (h, { row }) => {
                        const count = row.accounts ? row.accounts.length : 0;
                        return h(
                            "span",
                            { class: "ivu-tag ivu-tag-size-default ivu-tag-default" },
                            count
                        );
                    }
                },
                {
                    title: this.$t("actions"),
                    slot: "action",
                    align: "center",
                    width: 100
                }
            ];
        }
    },
    methods: {
        fetchData(attributes = {}) {
            this.loading = true;
            this.$store
                .dispatch("finance/bankProfile/fetch", {
                    ...attributes,
                    filter: { search: this.search }
                })
                .finally(() => (this.loading = false));
        },
        onEdit(row) {
            this.$store.commit("finance/bankProfile/SET_EDIT_DATA", row);
            this.$router.push({
                name: "bank-profile-edit",
                params: { id: row.bank_id }
            });
        },
        onDelete(row) {
            row._deleting = true;
            this.$store
                .dispatch("finance/bankProfile/destroy", row.bank_id)
                .then(response => {
                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                    this.fetchData();
                })
                .catch(error =>
                    this.notice({ type: "error", text: error.message })
                )
                .finally(() => (row._deleting = false));
        },
        onAddAccount(row) {
            this.addAccountBank = row;
            this.newAccount = emptyAccount();
            this.employeeOptions = [];
            this.errors = new Errors();
            this.addAccountModal = true;
        },
        onAddAccountCancel() {
            this.addAccountModal = false;
            this.addAccountBank = null;
            this.newAccount = emptyAccount();
        },
        onSaveAccount() {
            if (!this.addAccountBank) return;
            this.errors = new Errors();
            this.savingAccount = true;
            this.$store
                .dispatch("finance/bankProfile/storeAccount", {
                    bankId: this.addAccountBank.bank_id,
                    data: { ...this.newAccount, bank_id: this.addAccountBank.bank_id }
                })
                .then(response => {
                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                    if (response.message_id == 0) {
                        this.onAddAccountCancel();
                        this.fetchData();
                    }
                })
                .catch(error => {
                    this.errors = new Errors(error.errors);
                    this.notice({ type: "error", text: error.message });
                })
                .finally(() => (this.savingAccount = false));
        },
        onQrChange(event) {
            const file = event.target.files[0];
            if (!file) return;
            const formData = new FormData();
            formData.append("photo", file);
            this.uploadingQr = true;
            this.$store
                .dispatch("finance/bankProfile/qrUpload", formData)
                .then(path => { this.newAccount.qr_image = `/api/files?filePath=${path}`; })
                .finally(() => { this.uploadingQr = false; });
            event.target.value = "";
        },
        searchEmployeeForNew: debounce(function(query) {
            this.loadingEmployee = true;
            this.$store
                .dispatch("finance/bankProfile/searchEmployees", query)
                .then(data => { this.employeeOptions = data; })
                .finally(() => { this.loadingEmployee = false; });
        }, 400),
        notice(not) {
            this.$Notice[not.type]({
                title: "BANK PROFILE",
                desc: not.text
            });
        }
    },
    watch: {
        search: debounce(function() {
            this.fetchData();
        }, 500)
    },
    beforeRouteEnter(to, from, next) {
        next(vm => vm.fetchData());
    }
};
</script>
